import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setActiveModule, selectActiveModule, selectActiveModuleId } from "../../store/slices/moduleSlice";
import { getEnabledModules } from "../../config/modules";

export default function ModuleSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const activeModule = useAppSelector(selectActiveModule);
  const activeModuleId = useAppSelector(selectActiveModuleId);
  const modules = getEnabledModules();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleModuleChange = (module) => {
    if (module.id !== activeModuleId) {
      // Update module state first
      dispatch(setActiveModule(module.id));
      
      const currentPath = location.pathname;
      if (currentPath !== "/admin/dashboard") {
        requestAnimationFrame(() => {
          try {
            navigate("/admin/dashboard");
          } catch (error) {
            console.warn("Navigation error:", error);
          }
        });
      }
    }
    setIsOpen(false);
  };

  const getColorClasses = (color) => {
    const colorMap = {
      blue: "bg-blue-500 text-white",
      emerald: "bg-emerald-500 text-white",
      purple: "bg-purple-500 text-white",
      orange: "bg-orange-500 text-white",
    };
    return colorMap[color] || colorMap.emerald;
  };

  const getHoverColorClasses = (color) => {
    const colorMap = {
      blue: "hover:bg-blue-50 hover:text-blue-700",
      emerald: "hover:bg-emerald-50 hover:text-emerald-700",
      purple: "hover:bg-purple-50 hover:text-purple-700",
      orange: "hover:bg-orange-50 hover:text-orange-700",
    };
    return colorMap[color] || colorMap.emerald;
  };

  const getFocusRingClasses = (color) => {
    const colorMap = {
      blue: "focus:ring-blue-500/50",
      emerald: "focus:ring-emerald-500/50",
      purple: "focus:ring-purple-500/50",
      orange: "focus:ring-orange-500/50",
    };
    return colorMap[color] || colorMap.emerald;
  };

  if (!activeModule) return null;

  const ActiveIcon = activeModule.icon;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Module Switcher Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center px-3 py-2 space-x-2 text-sm font-medium rounded-lg
          transition-all duration-200 border
          ${getColorClasses(activeModule.color)}
          border-transparent
          hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2
          ${getFocusRingClasses(activeModule.color)}
        `}
        aria-label="Switch module"
      >
        <ActiveIcon size={18} className="flex-shrink-0" />
        <span className="hidden sm:block font-semibold">
          {activeModule.shortName}
        </span>
        <ChevronDown
          size={14}
          className={`flex-shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="absolute right-0 z-50 mt-2 w-64 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden"
            >
              {/* Header */}
              <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Switch Module
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Select a product line to manage
                </p>
              </div>

              {/* Module List */}
              <div className="py-2 max-h-96 overflow-y-auto">
                {modules.map((module) => {
                  const ModuleIcon = module.icon;
                  const isActive = module.id === activeModuleId;

                  return (
                    <button
                      key={module.id}
                      onClick={() => handleModuleChange(module)}
                      className={`
                        relative flex items-center w-full px-4 py-3 text-left
                        transition-all duration-150
                        ${isActive ? "bg-gray-50" : ""}
                        ${getHoverColorClasses(module.color)}
                      `}
                    >
                      {/* Active Indicator */}
                      {isActive && (
                        <div
                          className={`absolute left-0 top-0 bottom-0 w-1 ${getColorClasses(
                            module.color
                          )}`}
                        />
                      )}

                      {/* Icon */}
                      <div
                        className={`
                          flex items-center justify-center w-10 h-10 rounded-lg
                          ${getColorClasses(module.color)}
                          flex-shrink-0
                        `}
                      >
                        <ModuleIcon size={18} />
                      </div>

                      {/* Module Info */}
                      <div className="flex-1 ml-3 min-w-0">
                        <div className="flex items-center justify-between">
                          <p
                            className={`text-sm font-semibold ${
                              isActive ? "text-gray-900" : "text-gray-700"
                            }`}
                          >
                            {module.name}
                          </p>
                          {isActive && (
                            <Check
                              size={16}
                              className={`${
                                module.color === 'blue' ? 'text-blue-600' :
                                module.color === 'emerald' ? 'text-emerald-600' :
                                module.color === 'purple' ? 'text-purple-600' :
                                module.color === 'orange' ? 'text-orange-600' :
                                'text-emerald-600'
                              } flex-shrink-0 ml-2`}
                            />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {module.description}
                        </p>
                        {module.badge && (
                          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">
                            {module.badge}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

